import { useState } from "react";
import { useMutation } from "@apollo/client";
import {
  UPDATE_EMPLOYEE_ADMIN_STATUS,
  UPDATE_EMPLOYEE_ROLES,
  DELETE_EMPLOYEE_ROLES,
} from "../../../../apis/settingsApis/admin-apis/adminApis";
import Role from "../../../../utils/roles";

export const useAdminOperations = (
  refetch,
  setSnackbarInfo,
  TransitionComponent
) => {
  const [updateAdminStatus, { loading: loadAdmin }] = useMutation(
    UPDATE_EMPLOYEE_ADMIN_STATUS
  );
  const [updateEmployeeRoles, { loading: updateRolesLoading }] = useMutation(
    UPDATE_EMPLOYEE_ROLES
  );
  const [deleteEmployeeRoles, { loading: deleteRolesLoading }] = useMutation(
    DELETE_EMPLOYEE_ROLES
  );
  const [assignRoleLoading, setAssignRoleLoading] = useState(false);
  const [assigningRoleId, setAssigningRoleId] = useState(null);

  const handleAddAdmin = async (selectedStaff) => {
    const emails = selectedStaff?.map((staff) => staff?.email);
    try {
      const response = await updateAdminStatus({
        variables: { status: true, emails },
      });
      console.log(
        "Added admins successfully:",
        response?.data?.updateEmployeeAdminStatus?.message
      );
      setSnackbarInfo({
        message: "Added admin successfully!",
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    } catch (error) {
      console.error("Error adding admins:", error);
      setSnackbarInfo({
        message: "Failed to add admin. Please try again.",
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    }
    refetch();
  };

  const handleUpdateRoles = async (selectedMember, selectedRoles) => {
    if (!selectedMember) return;
    try {
      await updateEmployeeRoles({
        variables: {
          email: selectedMember?.email,
          roleIds: selectedRoles,
        },
      });
      setSnackbarInfo({
        message: `Updated roles for ${selectedMember?.name}`,
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
      refetch();
    } catch (error) {
      setSnackbarInfo({
        message: "Failed to update roles. Please try again.",
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    }
  };

  const handleRemoveSpecificRole = async (selectedMember, roleToRemove) => {
    if (!selectedMember || !roleToRemove) return;

    try {
      await deleteEmployeeRoles({
        variables: {
          email: selectedMember?.email,
          roleIds: [roleToRemove?.id],
        },
      });

      setSnackbarInfo({
        message: `Removed ${roleToRemove?.name} role from ${selectedMember?.name}`,
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });

      refetch();
    } catch (error) {
      setSnackbarInfo({
        message: "Failed to remove role. Please try again.",
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    }
  };

  const handleConfirmRemove = async (selectedMember) => {
    if (!selectedMember) return;

    const emails = [selectedMember.email];
    try {
      const response = await updateAdminStatus({
        variables: { status: false, emails },
      });

      await deleteEmployeeRoles({
        variables: {
          email: selectedMember?.email,
          roleIds:
            selectedMember?.roles
              ?.map((role) => parseInt(role?.role_id))
              .filter((id) => id) || [],
        },
      });
      console.log("Removed all admin roles successfully");

      setSnackbarInfo({
        message: `${selectedMember?.name} removed as admin and all admin roles.`,
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    } catch (error) {
      console.error("Error removing admin:", error);
      setSnackbarInfo({
        message: "Failed to remove admin. Please try again.",
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    }
    refetch();
  };

  const handleAssignRole = async (role, user, rolesData) => {
    setAssigningRoleId(role.id);
    setAssignRoleLoading(true);
    try {
      if (!user?.roles?.some((r) => r?.role_description === Role?.ADMIN)) {
        await updateAdminStatus({
          variables: { status: true, emails: [user?.email] },
        });
      }
      const adminRole = rolesData?.getRoles?.find(
        (r) => r?.role_description?.toLowerCase() === Role?.ADMIN?.toLowerCase()
      );
      const newRoleIds = [adminRole?.role_id, role.id];
      await updateEmployeeRoles({
        variables: { email: user?.email, roleIds: newRoleIds },
      });
      refetch();
    } finally {
      setAssigningRoleId(null);
      setAssignRoleLoading(false);
    }
  };

  return {
    updateAdminStatus,
    updateEmployeeRoles,
    loadAdmin,
    updateRolesLoading,
    assignRoleLoading,
    assigningRoleId,
    handleAddAdmin,
    handleUpdateRoles,
    handleRemoveSpecificRole,
    handleConfirmRemove,
    handleAssignRole,
    deleteRolesLoading,
  };
};
